'use client';

import { useRef } from 'react';

import { WidthOptions } from '@/types';
import { Text } from '@/ui';
import clsx from 'clsx';
import { useInView } from 'framer-motion';
import CountUp from 'react-countup';
import SVG from 'react-inlinesvg';

import { ThreeDee } from '../components/3d';
import { Separator } from '../components/separator';
import { BlockBase, Wrapper, getBlockWidthClassNames, getMarginClassNames } from './utils';

export interface StatItem {
  title?: string;
  number: string | number;
  subtitle: string;
  icon?: {
    svg: string;
  };
}

export interface StatsGridProps extends BlockBase {
  title?: string;
  stats: StatItem[];
  blockWidth?: WidthOptions;
  columns?: '2' | '3' | '4';
}

interface AnimatedNumberProps {
  value: string | number;
}

function AnimatedNumber({ value }: AnimatedNumberProps) {
  const ref = useRef<HTMLDivElement>(null);
  const isInView = useInView(ref, { once: true, margin: '-100px' });

  // Parse the numeric value and preserve formatting
  const numericValue = typeof value === 'string' ? parseFloat(value.replace(/[^\d.-]/g, '')) || 0 : value;

  // Determine suffix/prefix from original value
  let suffix = '';
  let prefix = '';

  if (typeof value === 'string') {
    if (value.includes('%')) suffix = '%';
    if (value.includes('k') || value.includes('K')) suffix = 'k';
    if (value.includes('+')) suffix = '+';
    // Add currency symbols as prefix if needed
    if (value.includes('£')) prefix = '£';
    if (value.includes('$')) prefix = '$';
  }

  return (
    <div ref={ref}>
      <Text element="div" elementStyle="h2">
        {isInView ? (
          <CountUp start={0} end={numericValue} duration={2} prefix={prefix} suffix={suffix} separator="," />
        ) : (
          '0'
        )}
      </Text>
    </div>
  );
}

export function StatsGrid({ title, stats, id, margin, blockWidth, columns = '3' }: StatsGridProps) {
  if (!stats || stats.length === 0) {
    return null;
  }

  return (
    <Wrapper
      id={id}
      className={clsx(
        'flex flex-col gap-8',
        getMarginClassNames(margin),
        getBlockWidthClassNames(blockWidth, 'narrow'),
      )}
    >
      {title && (
        <Text element="h2" elementStyle="h2" className="text-center">
          {title}
        </Text>
      )}

      <div className="relative flex items-center justify-center">
        <div
          className={clsx('z-10 grid w-full gap-6', {
            'grid-cols-1 sm:grid-cols-2': columns === '2',
            'grid-cols-1 sm:grid-cols-2 lg:grid-cols-3': columns === '3',
            'grid-cols-1 sm:grid-cols-2 lg:grid-cols-4': columns === '4',
          })}
        >
          {stats
            .filter((stat) => stat.number)
            .map((stat, index) => (
              <div key={index} className="flex justify-center">
                <ThreeDee className="group flex aspect-square flex-col items-center rounded-2xl px-12 py-6 backdrop-blur-lg">
                  {stat.icon?.svg && (
                    <SVG className="group-hover:animate-wobble text-primary mb-3 size-12" src={stat.icon.svg} />
                  )}

                  <AnimatedNumber value={stat.number} />

                  <Text element="div" elementStyle="h4">
                    {stat.title}
                  </Text>

                  {stat.subtitle && (
                    <Text element="div" className="text-muted-foreground" elementStyle="span">
                      {stat.subtitle}
                    </Text>
                  )}
                </ThreeDee>
              </div>
            ))}
        </div>

        <Separator
          className="absolute z-0 w-full"
          style={{
            maskImage:
              'linear-gradient(to right, transparent 0%, rgb(0,0,0) 120px, rgb(0,0,0) calc(100% - 120px), transparent 100%)',
          }}
        />
      </div>
    </Wrapper>
  );
}
